import React, {ComponentProps, FC, useEffect, useState} from 'react';
import {animated, useTransition} from 'react-spring';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious, useTimeout} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useEventHandler} from '../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {assertUnreachable} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {AppPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {CenteredPopover} from '../../../../libs/shared/ui/src/components/popovers/centeredPopover';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {getIsCustomer, getIsOnDevelopersPlan} from '../../../store/data/company/companySelectors';
import {navigateTopBarPopoverAction} from '../../../store/panels/app/appPanelActions';
import {AppPanelPopoverTypesEnum} from '../../../store/panels/app/appPanelState';
import {progressBarTransitionDurationInMs} from '../../startGuide/startGuideProgressBar';
import {
  bestPracticesCourseTaskSetupGuide,
  connectChannelTaskSetupGuide,
  inboxesVsChannelsCourseTaskSetupGuide,
  inviteTeammatesTaskSetupGuide,
  sandboxAnalyticsDemoTaskSetupGuide,
  sandboxChatDemoTaskSetupGuide,
  sandboxGeneralDemoTaskSetupGuide,
  sharedWorkspaceTaskSetupGuide,
} from '../admin/tasks/adminOnboardingTasks';
import {AdminSetupGuideHelpCard} from './adminSetupGuideHelpCard';
import {AdminSetupGuideNavItem} from './adminSetupGuideNavItem';
import {AdminSetupGuideResourceTask} from './adminSetupGuideResourceTask';
import {resourceTaskDefinitions} from './adminSetupGuideResourceTaskDefinitions';
import {AdminSetupGuideScreen} from './adminSetupGuideScreen';
import {AdminSetupGuideTask} from './adminSetupGuideTask';
import {
  AdminSetupGuideConcreteScreen,
  AdminSetupGuideScreensEnum,
  AdminSetupGuideScreenTasksAndProgress,
  AdminSetupGuideTasksAndProgress,
} from './adminSetupGuideTypes';

/*
 * Constants.
 */

export const taskDefinitions = {
  demos: [
    sandboxGeneralDemoTaskSetupGuide,
    sandboxAnalyticsDemoTaskSetupGuide,
    sandboxChatDemoTaskSetupGuide,
  ],
  setup: [
    sharedWorkspaceTaskSetupGuide,
    inboxesVsChannelsCourseTaskSetupGuide,
    connectChannelTaskSetupGuide,
    bestPracticesCourseTaskSetupGuide,
    inviteTeammatesTaskSetupGuide,
  ],
};

/*
 * Props.
 */

interface AdminSetupGuideProps {
  tasks: AdminSetupGuideTasksAndProgress;
  onClose: () => void;
  defaultScreen: AdminSetupGuideConcreteScreen;
  onDismissPermanentlyClick: () => void;
  panelId: AppPanelId;
}

interface AdminSetupGuideBasicScreenProps {
  style: ComponentProps<typeof StyledAdminSetupGuideScreen>['style'];
  onInternalTaskStart: () => void;
}

interface AdminSetupGuideTasksScreenProps extends AdminSetupGuideBasicScreenProps {
  tasks: AdminSetupGuideScreenTasksAndProgress;
}

/*
 * Style.
 */

const StyledContainerDiv = styled.div`
  width: 1084px;
  height: 692px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0px 3px 10px 0px rgba(0, 0, 0, 0.1), 0px 0px 1px 0px rgba(0, 0, 0, 0.3);
  background-color: ${(p) => p.theme.greys.white};
  display: flex;
`;

const StyledNavContainerDiv = styled.div`
  width: 240px;
  height: 692px;
  min-width: 240px;
  background-color: ${(p) => p.theme.greys.shade20};
  display: flex;
  flex-direction: column;
`;

const StyledNavMenuDiv = styled.div`
  display: flex;
  padding: 30px 14px;
  flex-direction: column;
  gap: 30px;
`;

const StyledNavMenuTitleDiv = styled.div`
  display: flex;
  padding: 0px 16px;
  font-size: 24px;
  font-weight: ${fontWeights.black};
  line-height: 130%;
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledNavMenuItemsDiv = styled.div`
  display: flex;
  padding: 0px 8px;
  flex-direction: column;
  gap: 8px;
`;

const StyledNavHelpCardContainerDiv = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-end;
`;

const StyledNavDismissContainerDiv = styled.div`
  padding: 0 16px 16px 16px;
  display: flex;
`;

const StyledContentContainerDiv = styled.div`
  position: relative;
  flex-grow: 1;
`;

const StyledAdminSetupGuideScreen = styled(animated(AdminSetupGuideScreen))`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: ${(p) => p.theme.greys.white};
`;

const StyledCloseButtonContainerDiv = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`;

/*
 * Component.
 */

const screens: ReadonlyArray<AdminSetupGuideConcreteScreen> = [
  AdminSetupGuideScreensEnum.DEMOS,
  AdminSetupGuideScreensEnum.SETUP,
  AdminSetupGuideScreensEnum.MORE,
];

const prevScreenStyle = {opacity: 0, transform: 'translateY(-10%)'};
const currentScreenStyle = {opacity: 1, transform: 'translateY(0%)'};
const nextScreenStyle = {opacity: 0, transform: 'translateY(10%)'};
const screenTransitionConfig = {mass: 0.25, tension: 250};

export const AdminSetupGuide: FC<AdminSetupGuideProps> = (props) => {
  const {onDismissPermanentlyClick, defaultScreen, panelId} = props;
  const [currentScreen, setCurrentScreen] = useState<AdminSetupGuideConcreteScreen>(defaultScreen);

  const dispatch = useAppDispatch();
  const theme = useUserTheme();
  const {trackDataEvent} = useDataEventTracking();

  const isCustomer = useAppSelector(getIsCustomer);
  const isDeveloper = useAppSelector(getIsOnDevelopersPlan);

  useEffect(() => {
    trackDataEvent(DataEventActionsEnum.VIEW, OnboardingDataEventObject.ADMIN_SETUP_GUIDE_SCREEN, {
      active_screen: currentScreen,
    });
  }, [trackDataEvent, currentScreen]);

  useAutomaticallyAdvanceScreen(props.tasks, defaultScreen, currentScreen, setCurrentScreen);

  const currentScreenIndexDiff = useScreenIndexDiff(currentScreen);
  const renderScreenTransitions = useTransition(currentScreen, {
    // Don't animate when the component first mounts.
    from: currentScreenIndexDiff === 0 ? undefined : computeEnterFromScreenStyle(currentScreenIndexDiff),
    enter: currentScreenStyle,
    leave: computeLeaveToScreenStyle(currentScreenIndexDiff),
    config: screenTransitionConfig,
  });

  return (
    <CenteredPopover onRequestClose={props.onClose}>
      <StyledContainerDiv>
        <StyledNavContainerDiv>
          <StyledNavMenuDiv>
            <StyledNavMenuTitleDiv>
              <Trans id="admin-setup-guide-title">Setup guide</Trans>
            </StyledNavMenuTitleDiv>
            <StyledNavMenuItemsDiv>
              <AdminSetupGuideNavItem
                title={<Trans id="admin-setup-guide-product-demos">Product demos</Trans>}
                isCompleted={props.tasks.demos.progress.hasCompletedAllTasks}
                isActive={currentScreen === AdminSetupGuideScreensEnum.DEMOS}
                onClick={() => {
                  setCurrentScreen(AdminSetupGuideScreensEnum.DEMOS);
                  trackDataEvent(
                    DataEventActionsEnum.CLICK,
                    OnboardingDataEventObject.ADMIN_SETUP_GUIDE_PRODUCT_DEMOS_TAB,
                  );
                }}
              />
              <AdminSetupGuideNavItem
                title={<Trans id="admin-setup-guide-setup-the-basics">Set up the basics</Trans>}
                isCompleted={props.tasks.setup.progress.hasCompletedAllTasks}
                isActive={currentScreen === AdminSetupGuideScreensEnum.SETUP}
                onClick={() => {
                  setCurrentScreen(AdminSetupGuideScreensEnum.SETUP);
                  trackDataEvent(
                    DataEventActionsEnum.CLICK,
                    OnboardingDataEventObject.ADMIN_SETUP_GUIDE_BASICS_CHECKLIST_TAB,
                  );
                }}
              />
              <AdminSetupGuideNavItem
                title={<Trans id="admin-setup-guide-get-more">Get more out of Front</Trans>}
                isActive={currentScreen === AdminSetupGuideScreensEnum.MORE}
                onClick={() => {
                  setCurrentScreen(AdminSetupGuideScreensEnum.MORE);
                  trackDataEvent(
                    DataEventActionsEnum.CLICK,
                    OnboardingDataEventObject.ADMIN_SETUP_GUIDE_GET_MORE_LMS_TAB,
                  );
                }}
              />
            </StyledNavMenuItemsDiv>
          </StyledNavMenuDiv>
          <StyledNavHelpCardContainerDiv>
            <AdminSetupGuideHelpCard
              onMoreResourcesClick={() => {
                props.onClose();
                dispatch(navigateTopBarPopoverAction(panelId, AppPanelPopoverTypesEnum.HELP));
              }}
            />
          </StyledNavHelpCardContainerDiv>
          {(isCustomer || isDeveloper) && (
            <StyledNavDismissContainerDiv>
              <Button variant={ButtonVariantsEnum.SECONDARY_DANGER} onClick={onDismissPermanentlyClick}>
                <Trans id="admin-setup-guide-dismiss-permanently-button">Dismiss permanently</Trans>
              </Button>
            </StyledNavDismissContainerDiv>
          )}
        </StyledNavContainerDiv>
        <StyledContentContainerDiv>
          {renderScreenTransitions((style, transitionScreen) => {
            switch (transitionScreen) {
              case AdminSetupGuideScreensEnum.DEMOS:
                return (
                  <DemosScreen style={style} tasks={props.tasks.demos} onInternalTaskStart={props.onClose} />
                );

              case AdminSetupGuideScreensEnum.SETUP:
                return (
                  <SetupScreen style={style} tasks={props.tasks.setup} onInternalTaskStart={props.onClose} />
                );

              case AdminSetupGuideScreensEnum.MORE:
                return <MoreScreen style={style} onInternalTaskStart={props.onClose} />;

              default:
                assertUnreachable(transitionScreen);
                return null;
            }
          })}
          <StyledCloseButtonContainerDiv>
            <IconButton
              iconName="close"
              label={<Trans id="admin-setup-guide-close">Close</Trans>}
              onClick={props.onClose}
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
            />
          </StyledCloseButtonContainerDiv>
        </StyledContentContainerDiv>
      </StyledContainerDiv>
    </CenteredPopover>
  );
};

const DemosScreen: FC<AdminSetupGuideTasksScreenProps> = (props) => (
  <StyledAdminSetupGuideScreen
    style={props.style}
    title={<Trans id="admin-setup-guide-demos-screen-title">Product demos</Trans>}
    description={
      <Trans id="admin-setup-guide-demos-screen-description">
        Here is a series of interactive demos to get you familiar with some key Front features.
      </Trans>
    }
    progress={props.tasks.progress}
  >
    {props.tasks.tasks.map((t, i) => (
      <AdminSetupGuideTask
        key={t.key}
        number={i + 1}
        title={t.title}
        description={t.description}
        details={t.details}
        isExternal={t.isExternal}
        isComplete={t.isComplete}
        startTask={t.startTask}
        onInternalTaskStart={props.onInternalTaskStart}
      />
    ))}
  </StyledAdminSetupGuideScreen>
);

const SetupScreen: FC<AdminSetupGuideTasksScreenProps> = (props) => (
  <StyledAdminSetupGuideScreen
    style={props.style}
    title={<Trans id="admin-setup-guide-setup-screen-title">Set up the basics</Trans>}
    description={
      <Trans id="admin-setup-guide-setup-screen-description">
        Complete basic setups to start experiencing Front with your company’s context.
      </Trans>
    }
    progress={props.tasks.progress}
  >
    {props.tasks.tasks.map((t, i) => (
      <AdminSetupGuideTask
        key={t.key}
        number={i + 1}
        title={t.title}
        description={t.description}
        details={t.details}
        isExternal={t.isExternal}
        isComplete={t.isComplete}
        startTask={t.startTask}
        onInternalTaskStart={props.onInternalTaskStart}
      />
    ))}
  </StyledAdminSetupGuideScreen>
);

const MoreScreen: FC<AdminSetupGuideBasicScreenProps> = (props) => (
  <StyledAdminSetupGuideScreen
    style={props.style}
    title={<Trans id="admin-setup-guide-more-screen-title">Get more out of Front</Trans>}
    description={
      <Trans id="admin-setup-guide-more-screen-description">
        Learn how to set up some powerful features to help achieve your team’s goals with Front.
      </Trans>
    }
    hasLargeRowGap
  >
    {resourceTaskDefinitions.map((task) => (
      <AdminSetupGuideResourceTask
        key={task.key}
        title={task.title}
        image={task.image}
        resources={task.resources}
        onInternalTaskStart={props.onInternalTaskStart}
      />
    ))}
  </StyledAdminSetupGuideScreen>
);

/*
 * Hooks.
 */

const automaticallyAdvanceScreenDelay = progressBarTransitionDurationInMs + 500;

// Automatically navigates to the next screen when a set of tasks has been completed.
function useAutomaticallyAdvanceScreen(
  tasks: AdminSetupGuideTasksAndProgress,
  defaultScreen: AdminSetupGuideConcreteScreen,
  currentScreen: AdminSetupGuideConcreteScreen,
  onScreenChange: (screen: AdminSetupGuideConcreteScreen) => void,
) {
  const previousTasks = usePrevious(tasks);
  const [safeSetTimeout] = useTimeout();

  const didCompleteDemoTasks =
    previousTasks?.demos.progress.hasCompletedAllTasks === false && tasks.demos.progress.hasCompletedAllTasks;

  const didCompleteSetupTasks =
    previousTasks?.setup.progress.hasCompletedAllTasks === false && tasks.setup.progress.hasCompletedAllTasks;

  const onScreenAdvance = useEventHandler(
    (fromScreen: AdminSetupGuideConcreteScreen, toScreen: AdminSetupGuideConcreteScreen) => {
      // Wait a few seconds for the progress bar CSS animations to complete.
      safeSetTimeout(() => {
        // Only auto-advance if we're on the right screen and the user hasn't manually navigated to a different screen.
        if (defaultScreen === currentScreen && currentScreen === fromScreen) {
          onScreenChange(toScreen);
        }
      }, automaticallyAdvanceScreenDelay);
    },
  );

  useEffect(() => {
    if (didCompleteDemoTasks) {
      onScreenAdvance(AdminSetupGuideScreensEnum.DEMOS, AdminSetupGuideScreensEnum.SETUP);
    } else if (didCompleteSetupTasks) {
      onScreenAdvance(AdminSetupGuideScreensEnum.SETUP, AdminSetupGuideScreensEnum.MORE);
    }
  }, [didCompleteDemoTasks, didCompleteSetupTasks, onScreenAdvance]);
}

/*
 * Helpers.
 */

function useScreenIndexDiff(screen: AdminSetupGuideConcreteScreen): number {
  const screenIndex = screens.indexOf(screen);
  const previousScreenIndex = usePrevious(screenIndex) ?? screenIndex;
  return screenIndex - previousScreenIndex;
}

function computeEnterFromScreenStyle(screenIndexDiff: number) {
  // When moving to the previous screen, the current screen will become the previous screen.
  return screenIndexDiff < 0 ? prevScreenStyle : nextScreenStyle;
}

function computeLeaveToScreenStyle(screenIndexDiff: number) {
  // When moving to the previous screen, the current screen will become the next screen.
  return screenIndexDiff < 0 ? nextScreenStyle : prevScreenStyle;
}
